<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Monthly Payroll Summary By Cost Center </v-card-title>
      <v-row class="px-4">
        <v-col cols="10">
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedPayrollPeriod"
                v-validate="'required'"
                label="Payroll Period"
                :item-text="item => this.$moment(item.payroll_date).format('YYYY-MM')"
                item-value="id"
                :items="payrollPeriods"
                clearable
                hide-selected
                :error-messages="errors.collect('selectedPayrollPeriod')"
                data-vv-name="selectedPayrollPeriod"  
                data-vv-as="payroll period"
                return-object
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="payrollCycle"
                v-validate="'required'"
                label="Payroll Cycles"
                item-text="payroll_cycle.name"
                item-value="payroll_cycle.id"
                :items="selectedPayrollPeriod ? selectedPayrollPeriod.payroll_cycle_transactions : []"
                :disabled="!selectedPayrollPeriod"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('payrollCycle')"
                data-vv-name="payrollCycle"  
                data-vv-as="payroll cycles"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedGroups"
                :items="groupOptions"
                label="Company Groups"
                item-text="text"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                clearable
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                label="Companies"
                item-text="name"
                item-value="id"
                :items="companyOptions"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"
              />
            </v-col>           
          </v-row>
          <v-row>
            <v-col>
              <v-checkbox v-model="showHeadcount">
                <template v-slot:label>
                  <div class="font-weight-bold">
                    Show Headcount in Report
                  </div>
                </template>
              </v-checkbox>
            </v-col>  
          </v-row>
        </v-col>
  
        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="loading" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="loading"
                @click="generateMonthlyPayrollReportByCostCenter"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      payrollPeriods: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        selectedGroups: [],
        selectedCompanies: [],
        progressbar: 0,
        selectedPayrollPeriod: null,
        payrollCycle: null,
        showHeadcount: true,
      }
    },
    computed: {
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
    },
    watch: {
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    methods: {
      generateMonthlyPayrollReportByCostCenter () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            const payroll = this.payrollPeriods.find(payrollPeriod => {
              return payrollPeriod.id === this.selectedPayrollPeriod.id
            })

            this.$api.payrollReports().monthlyPayrollReportByCostCenter(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'MonthlyPayrollReportByCostCenter - ' + this.$moment(payroll.payroll_date).format('MMM YYYY') + ' [' + this.$moment().format('YYYY-MM-DD h.mma') + '].xlsx') 
                link.click()
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(error => {
                this.loading = false
                if (error.response.status === 400) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.status, 
                  })
                } else {
                  this.$snack({ color: 'error', message: 'Error! Please contact support' })
                }
              })
          })
      },
      prepareFormData () {
        return {
          payrollPeriodId: this.selectedPayrollPeriod.id,
          payrollCycles: this.payrollCycle,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies,
          showHeadcount: this.showHeadcount,
        }
      },
    },
  }
</script>
